import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ActionButton, Buttons, CancelButton, Drawer, Icon, SingleSelect, Table, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

const MemberCell = styled(Table.Cell)`
  color: ${({ isActive }) => (isActive ? colors.black : colors.grey55)};
`;

export default function MemberMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const { xeroConnectionId } = useParams();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);

  const [{ members, contacts, isReady }, setState] = useState({ members: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: members } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getMemberMappings();
      const { data: contacts } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getContacts();

      if (!isMounted.current) return;
      setState({ members, contacts, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const updateMemberMapping = ({ memberId, xeroContactId }) => {
    setDirty(true);

    setState((state) => ({
      ...state,
      members: state.members.map((m) => (m.id === memberId ? { ...m, xeroContactId } : m)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Member Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            const mappings = members.map((member) => ({ memberId: member.id, xeroContactId: member.xeroContactId }));
            await api.www.workspaces(workspace.id).xero(xeroConnectionId).updateMemberMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="25%">Member</Table.Column>
                <Table.Column width="25%">Email</Table.Column>
                <Table.Column>Xero Contact</Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                {members.map((member) => {
                  const handleContactChange = (event) => {
                    updateMemberMapping({
                      memberId: member.id,
                      xeroContactId: event.target.value || null,
                    });
                  };

                  return (
                    <Table.BoxRow key={member.id}>
                      <MemberCell isActive={member.isActive}>{member.name}</MemberCell>
                      <Table.Cell>{member.email}</Table.Cell>
                      <Table.Cell>
                        <ContactSelect
                          contacts={contacts}
                          value={member.xeroContactId}
                          onChange={handleContactChange}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function ContactSelect({ contacts, value, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const options = useMemo(() => {
    if (!filterValue) return contacts;
    const fuse = new Fuse(contacts, { keys: ['name'], threshold: 0.1 });
    return fuse.search(filterValue).map((result) => result.item);
  }, [contacts, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const contact = _.find(contacts, { id: value });
    if (!contact)
      return {
        id: value,
        name: (
          <Tooltip message="Contact not found in Xero.">
            <Icon icon="warning" color={colors.warning} spaceRight />
            Contact ID: {value}
          </Tooltip>
        ),
      };
    return { id: contact.id, name: contact.name };
  }, [contacts, value]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </SingleSelect>
  );
}
